import { createNavigation } from 'next-intl/navigation'
import { defineRouting } from 'next-intl/routing'
import { getDomain } from '@/setup/domain'
import { Locale } from '@/setup/locales'
import { CityBrandRoute, CityBrandsRoute, ICityBrandUrlParams } from '@/setup/urls/brand-route'
import { CityRoute, ICityUrlParams } from '@/setup/urls/city-route'
import { ILpUrlProperties, LpRoute } from '@/setup/urls/lp-route'
import { IRestaurantUrlParams, RestaurantRoute } from '@/setup/urls/restaurant-route'
import { RestaurantsRoute } from '@/setup/urls/restaurants-route'
import { BlogRoute, IBlogUrlParams } from './urls/blog-route'

interface IUrlConfig {
    // biome-ignore lint/suspicious/noExplicitAny: biome migration
    getUrl: (params?: any) => string
    // biome-ignore lint/suspicious/noExplicitAny: biome migration
    defineParams: (params: any) => Record<string, any> | undefined
}

export const ALL_URL_PATTERNS = [
    '/' as const,
    '/restaurants' as const,
    '/restaurants/[citySlug]' as const,
    '/restaurants/[citySlug]/[restaurantSlug]' as const,
    '/restaurants/[citySlug]/brands' as const,
    '/restaurants/[citySlug]/brands/[brandSlug]' as const,
    '/lp/monate-universal' as const,
    '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]' as const,
    '/become-a-partner' as const,
    '/become-a-partner/[placeIdSlug]' as const,
    '/for-companies' as const,
    '/for-companies/codes' as const,
    '/for-companies/benefit' as const,
    '/for-companies/[placeIdSlug]' as const,
    '/terms-and-conditions' as const,
    '/event-deal-terms-and-conditions' as const,
    '/privacy-policy-partner' as const,
    '/partner-upload' as const,
    '/faq' as const,
    '/contact' as const,
    '/contact/mobile' as const,
    '/cheat-sheet' as const,
    '/redeem-deal' as const,
    '/imprint' as const,
    '/privacy-policy' as const,
    '/privacy-policy-app' as const,
    '/code' as const,
    '/pricing' as const,
    '/experiences' as const,
    '/download' as const,
    '/invite' as const,
    '/jobs' as const,
    '/gift-box' as const,
    '/blog' as const,
    '/blog/[slugId]' as const,
    '/blog/topic/[slug]' as const,
    '/special-offer' as const,
    '/tiktok-filter' as const,
    '/invitation' as const,
    '/urbansportsclub' as const,
    '/ditsch' as const,
    '/zenjob' as const,
    '/student-discounts' as const,
    '/student-discounts/zenjob' as const,
    '/lp/voucher-campaigns/[campaignName]/claim' as const,
]

export type IUrlPattern = (typeof ALL_URL_PATTERNS)[number]

export const UrlConfig = {
    ['/']: {
        getUrl: () => '/',
        defineParams: () => undefined,
    },

    ['/restaurants']: {
        getUrl: () => RestaurantsRoute.createUrl(),
        defineParams: () => undefined,
    },

    ['/restaurants/[citySlug]']: {
        getUrl: (params: ICityUrlParams) => CityRoute.createUrl(params),
        defineParams: (params: ICityUrlParams) => params,
    },

    ['/restaurants/[citySlug]/[restaurantSlug]']: {
        getUrl: (params: IRestaurantUrlParams) => RestaurantRoute.createUrl(params),
        defineParams: (params: IRestaurantUrlParams) => params,
    },

    ['/restaurants/[citySlug]/brands']: {
        getUrl: (params: ICityUrlParams) => CityBrandsRoute.createUrl(params),
        defineParams: (params: ICityUrlParams) => params,
    },

    ['/restaurants/[citySlug]/brands/[brandSlug]']: {
        getUrl: (params: ICityBrandUrlParams) => CityBrandRoute.createUrl(params),
        defineParams: (params: ICityBrandUrlParams) => params,
    },

    ['/lp/monate-universal']: {
        getUrl: () =>
            LpRoute.createUrl({ cityOrCountrySlug: null, customSlug: [] }, 'search-param'),
        defineParams: () => undefined,
    },

    ['/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]']: {
        getUrl: (params: ILpUrlProperties) => LpRoute.createUrl(params, 'search-param'),
        defineParams: (params: ILpUrlProperties) => params,
    },

    ['/become-a-partner']: {
        getUrl: () => '/become-a-partner',
        defineParams: () => undefined,
    },

    ['/become-a-partner/[placeIdSlug]']: {
        getUrl: (params: { placeIdSlug: string }) => `/become-a-partner/${params.placeIdSlug}`,
        defineParams: (params: { placeIdSlug: string }) => params,
    },

    ['/for-companies']: {
        getUrl: () => '/for-companies',
        defineParams: () => undefined,
    },

    ['/for-companies/codes']: {
        getUrl: () => '/for-companies/codes',
        defineParams: () => undefined,
    },

    ['/for-companies/benefit']: {
        getUrl: () => '/for-companies/benefit',
        defineParams: () => undefined,
    },

    ['/for-companies/[placeIdSlug]']: {
        getUrl: (params: { placeIdSlug: string }) => `/for-companies/${params.placeIdSlug}`,
        defineParams: (params: { placeIdSlug: string }) => params,
    },

    ['/terms-and-conditions']: {
        getUrl: () => `/terms-and-conditions`,
        defineParams: () => undefined,
    },

    ['/event-deal-terms-and-conditions']: {
        getUrl: () => `/event-deal-terms-and-conditions`,
        defineParams: () => undefined,
    },

    ['/privacy-policy-partner']: {
        getUrl: () => `/privacy-policy-partner`,
        defineParams: () => undefined,
    },

    ['/partner-upload']: {
        getUrl: () => `/partner-upload`,
        defineParams: () => undefined,
    },

    ['/faq']: {
        getUrl: () => `/faq`,
        defineParams: () => undefined,
    },

    ['/contact']: {
        getUrl: () => `/contact`,
        defineParams: () => undefined,
    },

    ['/contact/mobile']: {
        getUrl: () => `/contact/mobile`,
        defineParams: () => undefined,
    },

    ['/cheat-sheet']: {
        getUrl: () => `/cheat-sheet`,
        defineParams: () => undefined,
    },

    ['/redeem-deal']: {
        getUrl: () => `/redeem-deal`,
        defineParams: () => undefined,
    },

    ['/imprint']: {
        getUrl: () => `/imprint`,
        defineParams: () => undefined,
    },

    ['/privacy-policy']: {
        getUrl: () => `/privacy-policy`,
        defineParams: () => undefined,
    },

    ['/privacy-policy-app']: {
        getUrl: () => `/privacy-policy-app`,
        defineParams: () => undefined,
    },

    ['/code']: {
        getUrl: () => `/code`,
        defineParams: () => undefined,
    },

    ['/pricing']: {
        getUrl: () => `/pricing`,
        defineParams: () => undefined,
    },

    ['/experiences']: {
        getUrl: () => `/experiences`,
        defineParams: () => undefined,
    },

    ['/download']: {
        getUrl: () => `/download`,
        defineParams: () => undefined,
    },

    ['/invite']: {
        getUrl: () => `/invite`,
        defineParams: () => undefined,
    },

    ['/jobs']: {
        getUrl: () => `/jobs`,
        defineParams: () => undefined,
    },

    ['/gift-box']: {
        getUrl: () => `/gift-box`,
        defineParams: () => undefined,
    },

    ['/blog']: {
        getUrl: () => '/blog',
        defineParams: () => undefined,
    },

    ['/blog/[slugId]']: {
        getUrl: (params: IBlogUrlParams) => BlogRoute.createUrl(params),
        defineParams: (params: IBlogUrlParams) => params,
    },

    ['/blog/topic/[slug]']: {
        getUrl: (params: { slug: string }) => `/blog/topic/${params.slug}`,
        defineParams: (params: { slug: string }) => params,
    },
    ['/special-offer']: {
        getUrl: () => '/special-offer',
        defineParams: () => undefined,
    },
    ['/tiktok-filter']: {
        getUrl: () => '/tiktok-filter',
        defineParams: () => undefined,
    },
    ['/invitation']: {
        getUrl: () => '/invitation',
        defineParams: () => undefined,
    },

    ['/urbansportsclub']: {
        getUrl: () => '/urbansportsclub',
        defineParams: () => undefined,
    },

    ['/ditsch']: {
        getUrl: () => '/ditsch',
        defineParams: () => undefined,
    },

    ['/zenjob']: {
        getUrl: () => '/zenjob',
        defineParams: () => undefined,
    },

    ['/student-discounts']: {
        getUrl: () => '/student-discounts',
        defineParams: () => undefined,
    },

    ['/student-discounts/zenjob']: {
        getUrl: () => '/student-discounts/zenjob',
        defineParams: () => undefined,
    },

    ['/lp/voucher-campaigns/[campaignName]/claim']: {
        getUrl: (params: { campaignName: string }) =>
            `/lp/voucher-campaigns/${params.campaignName}/claim`,
        defineParams: (params: { campaignName: string }) => params,
    },
} satisfies Record<IUrlPattern, IUrlConfig>

/**
 * This is the configuration for the localized pathnames.
 *
 * The keys are the pathnames that are used in the application.
 * The values are the configured localized pathnames.
 */
export const LocalizedUrlConfig = {
    ['/']: {
        [Locale.DE_DE]: '/',
        [Locale.DE_AT]: '/',

        [Locale.EN_GB]: '/',
        [Locale.EN_DE]: '/',
        [Locale.EN_NL]: '/',
        [Locale.EN_AT]: '/',

        [Locale.NL_NL]: '/',
    },

    ['/restaurants']: {
        [Locale.DE_DE]: '/restaurants',
        [Locale.DE_AT]: '/restaurants',

        [Locale.EN_GB]: '/restaurants',
        [Locale.EN_DE]: '/restaurants',
        [Locale.EN_NL]: '/restaurants',
        [Locale.EN_AT]: '/restaurants',

        [Locale.NL_NL]: '/restaurants',
    },

    ['/restaurants/[citySlug]']: {
        [Locale.DE_DE]: '/restaurants/[citySlug]',
        [Locale.DE_AT]: '/restaurants/[citySlug]',

        [Locale.EN_GB]: '/restaurants/[citySlug]',
        [Locale.EN_DE]: '/restaurants/[citySlug]',
        [Locale.EN_NL]: '/restaurants/[citySlug]',
        [Locale.EN_AT]: '/restaurants/[citySlug]',

        [Locale.NL_NL]: '/restaurants/[citySlug]',
    },

    ['/restaurants/[citySlug]/[restaurantSlug]']: {
        [Locale.DE_DE]: '/restaurants/[citySlug]/[restaurantSlug]',
        [Locale.DE_AT]: '/restaurants/[citySlug]/[restaurantSlug]',

        [Locale.EN_GB]: '/restaurants/[citySlug]/[restaurantSlug]',
        [Locale.EN_DE]: '/restaurants/[citySlug]/[restaurantSlug]',
        [Locale.EN_NL]: '/restaurants/[citySlug]/[restaurantSlug]',
        [Locale.EN_AT]: '/restaurants/[citySlug]/[restaurantSlug]',

        [Locale.NL_NL]: '/restaurants/[citySlug]/[restaurantSlug]',
    },

    ['/restaurants/[citySlug]/brands']: {
        [Locale.DE_DE]: '/restaurants/[citySlug]/marken',
        [Locale.DE_AT]: '/restaurants/[citySlug]/marken',

        [Locale.EN_GB]: '/restaurants/[citySlug]/brands',
        [Locale.EN_DE]: '/restaurants/[citySlug]/brands',
        [Locale.EN_NL]: '/restaurants/[citySlug]/brands',
        [Locale.EN_AT]: '/restaurants/[citySlug]/brands',

        [Locale.NL_NL]: '/restaurants/[citySlug]/merken',
    },

    ['/restaurants/[citySlug]/brands/[brandSlug]']: {
        [Locale.DE_DE]: '/restaurants/[citySlug]/marken/[brandSlug]',
        [Locale.DE_AT]: '/restaurants/[citySlug]/marken/[brandSlug]',

        [Locale.EN_GB]: '/restaurants/[citySlug]/brands/[brandSlug]',
        [Locale.EN_DE]: '/restaurants/[citySlug]/brands/[brandSlug]',
        [Locale.EN_NL]: '/restaurants/[citySlug]/brands/[brandSlug]',
        [Locale.EN_AT]: '/restaurants/[citySlug]/brands/[brandSlug]',

        [Locale.NL_NL]: '/restaurants/[citySlug]/merken/[brandSlug]',
    },

    ['/lp/monate-universal']: {
        [Locale.DE_DE]: '/lp/monate-universal',
        [Locale.DE_AT]: '/lp/monate-universal',

        [Locale.EN_GB]: '/lp/monate-universal',
        [Locale.EN_DE]: '/lp/monate-universal',
        [Locale.EN_NL]: '/lp/monate-universal',
        [Locale.EN_AT]: '/lp/monate-universal',

        [Locale.NL_NL]: '/lp/maanden-universeel',
    },

    ['/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]']: {
        [Locale.DE_DE]: '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]',
        [Locale.DE_AT]: '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]',

        [Locale.EN_GB]: '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]',
        [Locale.EN_DE]: '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]',
        [Locale.EN_NL]: '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]',
        [Locale.EN_AT]: '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]',

        [Locale.NL_NL]: '/lp/maanden-universeel/[cityOrCountrySlug]/[a]/[[...customSlug]]',
    },

    ['/become-a-partner']: {
        [Locale.DE_DE]: '/partner-werden',
        [Locale.DE_AT]: '/partner-werden',

        [Locale.EN_GB]: '/become-a-partner',
        [Locale.EN_DE]: '/become-a-partner',
        [Locale.EN_NL]: '/become-a-partner',
        [Locale.EN_AT]: '/become-a-partner',

        [Locale.NL_NL]: '/partner-worden',
    },

    ['/become-a-partner/[placeIdSlug]']: {
        [Locale.DE_DE]: '/partner-werden/[placeIdSlug]',
        [Locale.DE_AT]: '/partner-werden/[placeIdSlug]',

        [Locale.EN_GB]: '/become-a-partner/[placeIdSlug]',
        [Locale.EN_DE]: '/become-a-partner/[placeIdSlug]',
        [Locale.EN_NL]: '/become-a-partner/[placeIdSlug]',
        [Locale.EN_AT]: '/become-a-partner/[placeIdSlug]',

        [Locale.NL_NL]: '/partner-worden/[placeIdSlug]',
    },

    ['/for-companies']: {
        [Locale.DE_DE]: '/fuer-unternehmen',
        [Locale.DE_AT]: '/fuer-unternehmen',

        [Locale.EN_GB]: '/for-companies',
        [Locale.EN_DE]: '/for-companies',
        [Locale.EN_NL]: '/for-companies',
        [Locale.EN_AT]: '/for-companies',

        [Locale.NL_NL]: '/voor-bedrijven',
    },

    ['/for-companies/codes']: {
        [Locale.DE_DE]: '/fuer-unternehmen/codes',
        [Locale.DE_AT]: '/fuer-unternehmen/codes',

        [Locale.EN_GB]: '/for-companies/codes',
        [Locale.EN_DE]: '/for-companies/codes',
        [Locale.EN_NL]: '/for-companies/codes',
        [Locale.EN_AT]: '/for-companies/codes',

        [Locale.NL_NL]: '/voor-bedrijven/codes',
    },

    ['/for-companies/benefit']: {
        [Locale.DE_DE]: '/fuer-unternehmen/benefit',
        [Locale.DE_AT]: '/fuer-unternehmen/benefit',

        [Locale.EN_GB]: '/for-companies/benefit',
        [Locale.EN_DE]: '/for-companies/benefit',
        [Locale.EN_NL]: '/for-companies/benefit',
        [Locale.EN_AT]: '/for-companies/benefit',

        [Locale.NL_NL]: '/voor-bedrijven/benefit',
    },

    ['/for-companies/[placeIdSlug]']: {
        [Locale.DE_DE]: '/fuer-unternehmen/[placeIdSlug]',
        [Locale.DE_AT]: '/fuer-unternehmen/[placeIdSlug]',

        [Locale.EN_GB]: '/for-companies/[placeIdSlug]',
        [Locale.EN_DE]: '/for-companies/[placeIdSlug]',
        [Locale.EN_NL]: '/for-companies/[placeIdSlug]',
        [Locale.EN_AT]: '/for-companies/[placeIdSlug]',

        [Locale.NL_NL]: '/voor-bedrijven/[placeIdSlug]',
    },

    ['/terms-and-conditions']: {
        [Locale.DE_DE]: '/agb',
        [Locale.DE_AT]: '/agb',

        [Locale.EN_GB]: '/terms-and-conditions',
        [Locale.EN_DE]: '/terms-and-conditions',
        [Locale.EN_NL]: '/terms-and-conditions',
        [Locale.EN_AT]: '/terms-and-conditions',

        [Locale.NL_NL]: '/algemene-voorwaarden',
    },

    ['/event-deal-terms-and-conditions']: {
        [Locale.DE_DE]: '/event-deal-agb',
        [Locale.DE_AT]: '/event-deal-agb',

        [Locale.EN_GB]: '/event-deal-terms-and-conditions',
        [Locale.EN_DE]: '/event-deal-terms-and-conditions',
        [Locale.EN_NL]: '/event-deal-terms-and-conditions',
        [Locale.EN_AT]: '/event-deal-terms-and-conditions',

        [Locale.NL_NL]: '/event-deal-voorwaarden',
    },

    ['/privacy-policy-partner']: {
        [Locale.DE_DE]: '/agb-partner',
        [Locale.DE_AT]: '/agb-partner',

        [Locale.EN_GB]: '/privacy-policy-partner',
        [Locale.EN_DE]: '/privacy-policy-partner',
        [Locale.EN_NL]: '/privacy-policy-partner',
        [Locale.EN_AT]: '/privacy-policy-partner',

        [Locale.NL_NL]: '/algemene-voorwaarden-partner',
    },

    ['/partner-upload']: {
        [Locale.DE_DE]: '/partner-upload',
        [Locale.DE_AT]: '/partner-upload',

        [Locale.EN_GB]: '/partner-upload',
        [Locale.EN_DE]: '/partner-upload',
        [Locale.EN_NL]: '/partner-upload',
        [Locale.EN_AT]: '/partner-upload',

        [Locale.NL_NL]: '/partner-upload',
    },

    ['/faq']: {
        [Locale.DE_DE]: '/faq',
        [Locale.DE_AT]: '/faq',

        [Locale.EN_GB]: '/faq',
        [Locale.EN_DE]: '/faq',
        [Locale.EN_NL]: '/faq',
        [Locale.EN_AT]: '/faq',

        [Locale.NL_NL]: '/faq',
    },

    ['/contact']: {
        [Locale.DE_DE]: '/kontakt',
        [Locale.DE_AT]: '/kontakt',

        [Locale.EN_GB]: '/contact',
        [Locale.EN_DE]: '/contact',
        [Locale.EN_NL]: '/contact',
        [Locale.EN_AT]: '/contact',

        [Locale.NL_NL]: '/contact',
    },

    ['/contact/mobile']: {
        [Locale.DE_DE]: '/kontakt/mobile',
        [Locale.DE_AT]: '/kontakt/mobile',

        [Locale.EN_GB]: '/contact/mobile',
        [Locale.EN_DE]: '/contact/mobile',
        [Locale.EN_NL]: '/contact/mobile',
        [Locale.EN_AT]: '/contact/mobile',

        [Locale.NL_NL]: '/contact/mobile',
    },

    ['/cheat-sheet']: {
        [Locale.DE_DE]: '/spickzettel',
        [Locale.DE_AT]: '/spickzettel',

        [Locale.EN_GB]: '/cheat-sheet',
        [Locale.EN_DE]: '/cheat-sheet',
        [Locale.EN_NL]: '/cheat-sheet',
        [Locale.EN_AT]: '/cheat-sheet',

        [Locale.NL_NL]: '/spiekbriefje',
    },

    ['/redeem-deal']: {
        [Locale.DE_DE]: '/deal-einloesen',
        [Locale.DE_AT]: '/deal-einloesen',

        [Locale.EN_GB]: '/redeem-deal',
        [Locale.EN_DE]: '/redeem-deal',
        [Locale.EN_NL]: '/redeem-deal',
        [Locale.EN_AT]: '/redeem-deal',

        [Locale.NL_NL]: '/deal-inwisselen',
    },

    ['/imprint']: {
        [Locale.DE_DE]: '/impressum',
        [Locale.DE_AT]: '/impressum',

        [Locale.EN_GB]: '/imprint',
        [Locale.EN_DE]: '/imprint',
        [Locale.EN_NL]: '/imprint',
        [Locale.EN_AT]: '/imprint',

        [Locale.NL_NL]: '/bedrijfsinformatie',
    },

    ['/privacy-policy']: {
        [Locale.DE_DE]: '/datenschutz',
        [Locale.DE_AT]: '/datenschutz',

        [Locale.EN_GB]: '/privacy-policy',
        [Locale.EN_DE]: '/privacy-policy',
        [Locale.EN_NL]: '/privacy-policy',
        [Locale.EN_AT]: '/privacy-policy',

        [Locale.NL_NL]: '/privacybeleid',
    },

    ['/privacy-policy-app']: {
        [Locale.DE_DE]: '/datenschutz-app',
        [Locale.DE_AT]: '/datenschutz-app',

        [Locale.EN_GB]: '/privacy-policy-app',
        [Locale.EN_DE]: '/privacy-policy-app',
        [Locale.EN_NL]: '/privacy-policy-app',
        [Locale.EN_AT]: '/privacy-policy-app',

        [Locale.NL_NL]: '/privacybeleid-app',
    },

    ['/code']: {
        [Locale.DE_DE]: '/code',
        [Locale.DE_AT]: '/code',

        [Locale.EN_GB]: '/code',
        [Locale.EN_DE]: '/code',
        [Locale.EN_NL]: '/code',
        [Locale.EN_AT]: '/code',

        [Locale.NL_NL]: '/code',
    },

    ['/pricing']: {
        [Locale.DE_DE]: '/preise',
        [Locale.DE_AT]: '/preise',

        [Locale.EN_GB]: '/pricing',
        [Locale.EN_DE]: '/pricing',
        [Locale.EN_NL]: '/pricing',
        [Locale.EN_AT]: '/pricing',

        [Locale.NL_NL]: '/kosten',
    },

    ['/experiences']: {
        [Locale.DE_DE]: '/erfahrungen',
        [Locale.DE_AT]: '/erfahrungen',

        [Locale.EN_GB]: '/experiences',
        [Locale.EN_DE]: '/experiences',
        [Locale.EN_NL]: '/experiences',
        [Locale.EN_AT]: '/experiences',

        [Locale.NL_NL]: '/ervaringen',
    },

    ['/download']: {
        [Locale.DE_DE]: '/download',
        [Locale.DE_AT]: '/download',

        [Locale.EN_GB]: '/download',
        [Locale.EN_DE]: '/download',
        [Locale.EN_NL]: '/download',
        [Locale.EN_AT]: '/download',

        [Locale.NL_NL]: '/download',
    },

    ['/invite']: {
        [Locale.DE_DE]: '/einladen',
        [Locale.DE_AT]: '/einladen',

        [Locale.EN_GB]: '/invite',
        [Locale.EN_DE]: '/invite',
        [Locale.EN_NL]: '/invite',
        [Locale.EN_AT]: '/invite',

        [Locale.NL_NL]: '/uitnodigen',
    },

    ['/jobs']: {
        [Locale.DE_DE]: '/jobs',
        [Locale.DE_AT]: '/jobs',

        [Locale.EN_GB]: '/jobs',
        [Locale.EN_DE]: '/jobs',
        [Locale.EN_NL]: '/jobs',
        [Locale.EN_AT]: '/jobs',

        [Locale.NL_NL]: '/vacatures',
    },

    ['/gift-box']: {
        [Locale.DE_DE]: '/geschenkbox',
        [Locale.DE_AT]: '/geschenkbox',

        [Locale.EN_GB]: '/gift-box',
        [Locale.EN_DE]: '/gift-box',
        [Locale.EN_NL]: '/gift-box',
        [Locale.EN_AT]: '/gift-box',

        [Locale.NL_NL]: '/cadeaubox',
    },

    ['/blog']: {
        [Locale.DE_DE]: '/blog',
        [Locale.DE_AT]: '/blog',

        [Locale.EN_GB]: '/blog',
        [Locale.EN_DE]: '/blog',
        [Locale.EN_NL]: '/blog',
        [Locale.EN_AT]: '/blog',

        [Locale.NL_NL]: '/blog',
    },

    ['/blog/[slugId]']: {
        [Locale.DE_DE]: '/blog/[slugId]',
        [Locale.DE_AT]: '/blog/[slugId]',

        [Locale.EN_GB]: '/blog/[slugId]',
        [Locale.EN_DE]: '/blog/[slugId]',
        [Locale.EN_NL]: '/blog/[slugId]',
        [Locale.EN_AT]: '/blog/[slugId]',

        [Locale.NL_NL]: '/blog/[slugId]',
    },

    ['/blog/topic/[slug]']: {
        [Locale.DE_DE]: '/blog/thema/[slug]',
        [Locale.DE_AT]: '/blog/thema/[slug]',

        [Locale.EN_GB]: '/blog/topic/[slug]',
        [Locale.EN_DE]: '/blog/topic/[slug]',
        [Locale.EN_NL]: '/blog/topic/[slug]',
        [Locale.EN_AT]: '/blog/topic/[slug]',

        [Locale.NL_NL]: '/blog/thema/[slug]',
    },

    ['/special-offer']: {
        [Locale.DE_DE]: '/special-offer',
        [Locale.DE_AT]: '/special-offer',
        [Locale.EN_GB]: '/special-offer',
        [Locale.EN_DE]: '/special-offer',
        [Locale.EN_NL]: '/special-offer',
        [Locale.EN_AT]: '/special-offer',
        [Locale.NL_NL]: '/special-offer',
    },

    ['/tiktok-filter']: {
        [Locale.DE_DE]: '/tiktok-filter',
        [Locale.DE_AT]: '/tiktok-filter',
        [Locale.EN_GB]: '/tiktok-filter',
        [Locale.EN_DE]: '/tiktok-filter',
        [Locale.EN_NL]: '/tiktok-filter',
        [Locale.EN_AT]: '/tiktok-filter',
        [Locale.NL_NL]: '/tiktok-filter',
    },

    ['/invitation']: {
        [Locale.DE_DE]: '/invitation',
        [Locale.DE_AT]: '/invitation',

        [Locale.EN_GB]: '/invitation',
        [Locale.EN_DE]: '/invitation',
        [Locale.EN_NL]: '/invitation',
        [Locale.EN_AT]: '/invitation',

        [Locale.NL_NL]: '/invitation',
    },

    ['/urbansportsclub']: {
        [Locale.DE_DE]: '/urbansportsclub',
        [Locale.DE_AT]: '/urbansportsclub',

        [Locale.EN_GB]: '/urbansportsclub',
        [Locale.EN_DE]: '/urbansportsclub',
        [Locale.EN_NL]: '/urbansportsclub',
        [Locale.EN_AT]: '/urbansportsclub',

        [Locale.NL_NL]: '/urbansportsclub',
    },

    ['/ditsch']: {
        [Locale.DE_DE]: '/ditsch',
        [Locale.DE_AT]: '/ditsch',

        [Locale.EN_GB]: '/ditsch',
        [Locale.EN_DE]: '/ditsch',
        [Locale.EN_NL]: '/ditsch',
        [Locale.EN_AT]: '/ditsch',

        [Locale.NL_NL]: '/ditsch',
    },

    ['/zenjob']: {
        [Locale.DE_DE]: '/zenjob',
        [Locale.DE_AT]: '/zenjob',

        [Locale.EN_GB]: '/zenjob',
        [Locale.EN_DE]: '/zenjob',
        [Locale.EN_NL]: '/zenjob',
        [Locale.EN_AT]: '/zenjob',

        [Locale.NL_NL]: '/zenjob',
    },

    ['/student-discounts']: {
        [Locale.DE_DE]: '/studentenrabatt',
        [Locale.DE_AT]: '/studentenrabatt',

        [Locale.EN_GB]: '/student-discounts',
        [Locale.EN_DE]: '/student-discounts',
        [Locale.EN_NL]: '/student-discounts',
        [Locale.EN_AT]: '/student-discounts',

        [Locale.NL_NL]: '/studentenkorting',
    },

    ['/student-discounts/zenjob']: {
        [Locale.DE_DE]: '/studentenrabatt/zenjob',
        [Locale.DE_AT]: '/studentenrabatt/zenjob',

        [Locale.EN_GB]: '/student-discounts/zenjob',
        [Locale.EN_DE]: '/student-discounts/zenjob',
        [Locale.EN_NL]: '/student-discounts/zenjob',
        [Locale.EN_AT]: '/student-discounts/zenjob',

        [Locale.NL_NL]: '/studentenkorting/zenjob',
    },

    // Not localized: the path is used verbatim in ad links, so it has to stay
    // stable across locales.
    ['/lp/voucher-campaigns/[campaignName]/claim']: {
        [Locale.DE_DE]: '/lp/voucher-campaigns/[campaignName]/claim',
        [Locale.DE_AT]: '/lp/voucher-campaigns/[campaignName]/claim',

        [Locale.EN_GB]: '/lp/voucher-campaigns/[campaignName]/claim',
        [Locale.EN_DE]: '/lp/voucher-campaigns/[campaignName]/claim',
        [Locale.EN_NL]: '/lp/voucher-campaigns/[campaignName]/claim',
        [Locale.EN_AT]: '/lp/voucher-campaigns/[campaignName]/claim',

        [Locale.NL_NL]: '/lp/voucher-campaigns/[campaignName]/claim',
    },
} satisfies Record<IUrlPattern, Record<Locale, string>>

export const DEFAULT_LOCALE = Locale.EN_DE

export const LOCALE_PREFIX_MAP = {
    'en-GB': '/gb' as const,
    'en-DE': '/de/en' as const,
    'en-NL': '/nl/en' as const,
    'en-AT': '/at/en' as const,

    'de-DE': '/de' as const,
    'de-AT': '/at' as const,

    'nl-NL': '/nl' as const,
} satisfies Record<Locale, string>

export const routing = defineRouting({
    locales: Object.values(Locale),
    defaultLocale: DEFAULT_LOCALE,
    localePrefix: {
        mode: 'always',
        prefixes: LOCALE_PREFIX_MAP,
    },
    pathnames: LocalizedUrlConfig,
})

export const {
    redirect,
    usePathname,
    useRouter: useRawNextIntlRouter,
    getPathname: rawGetPathname,
} = createNavigation(routing)

export function getPathname<T extends IUrlPattern>(
    pattern: T,
    params: IUrlParams<T>,
    locale: Locale | string
) {
    return rawGetPathname({
        // @ts-expect-error
        href: {
            params,
            pathname: pattern,
        },
        locale: locale as Locale,
    })
}

export type IUrlParams<T extends IUrlPattern> = Parameters<(typeof UrlConfig)[T]['defineParams']>[0]

// --- Link ---

export type ILinkParams<T extends IUrlPattern = IUrlPattern> = {
    pattern: T
    params: IUrlParams<T>
}

export const getLinkHref = <T extends IUrlPattern>(
    { params, pattern }: ILinkParams<T>,
    locale: Locale | string
) => getPathname(pattern, params, locale)

export const getShareableUrl = (url: string, locale: Locale | string) => {
    const rawDomain = getDomain(locale)

    const domain = rawDomain.endsWith('/') ? rawDomain.slice(0, -1) : rawDomain

    const normalizedUrl = url.startsWith('/') ? url : `/${url}`

    return `${domain}${normalizedUrl}`
}
